<script setup lang="ts">
import { ImageBySize } from '@/utils/image';

const { images = [], imageClasses } = defineProps<{
  carouselName: string;
  imageClasses: string;
  images?: ImageBySize[];
  prioritizeFirstImage?: boolean;
}>();

/**
 * Missing:
 * - Swipe support for desktop
 */

const currentIndex = defineModel<number>({ required: true });

let touchstartX = 0;
let touchmoveX = 0;
let isSwiping = false;

const startSwipe = (e: TouchEvent | MouseEvent) => {
  isSwiping = true;
  if (e instanceof TouchEvent) {
    touchstartX = e.touches[0].clientX;
  } else {
    touchstartX = e.clientX;
  }
};

const moveSwipe = (e: TouchEvent | MouseEvent) => {
  if (!isSwiping) return;
  if (e instanceof TouchEvent) {
    touchmoveX = e.touches[0].clientX;
  } else {
    touchmoveX = e.clientX;
  }
};

const endSwipe = () => {
  if (!isSwiping) return;
  isSwiping = false;
  const distance = touchmoveX - touchstartX;
  const threshold = 50;
  if (distance > threshold) {
    // Swipe right
    if (currentIndex.value > 0) {
      currentIndex.value -= 1;
    }
  } else if (distance < -threshold) {
    // Swipe left
    if (currentIndex.value < images.length - 1) {
      currentIndex.value += 1;
    }
  }
};
</script>

<template>
  <div class="relative overflow-hidden" aria-label="Carousel" role="group">
    <div
      class="flex transition-transform duration-500 ease-in-out snap-x snap-mandatory carousel scrollbar-hide"
      aria-live="polite"
      @touchstart="startSwipe"
      @touchmove="moveSwipe"
      @touchend="endSwipe"
    >
      <div
        v-for="(image, index) in images"
        :key="image.url"
        class="w-full shrink-0 snap-always snap-center snap-mandatory"
        role="tabpanel"
        :id="`slide-${carouselName}-${index}`"
        :aria-label="`Slide ${index + 1} of ${images.length}`"
        :aria-hidden="currentIndex !== index"
      >
        <img
          :alt="`Image ${index + 1}`"
          :class="imageClasses"
          :fetchpriority="prioritizeFirstImage && index === 0 ? 'high' : 'low'"
          :loading="prioritizeFirstImage && index === 0 ? 'eager' : 'lazy'"
          :src="image.medium"
        />
      </div>
    </div>
    <slot />
  </div>
</template>

<style scoped>
.carousel {
  transform: translateX(v-bind('`-${currentIndex * 100}%`'));
}
</style>
